import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import moment from 'moment';
import i18n from 'i18n';
import ContentLoader from '../../ContentLoader';
import CmtImage from '../../../../@coremat/CmtImage';
import AuthWrapper from './AuthWrapper';
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Hidden,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  BypassImpersonationSsoInDev,
  homePage,
  HOST,
  IsClub,
  IsDev,
  IsDSM,
  IsEmployee,
  IsImpersonationMode,
  IsLocalDevOrServerDev,
  IsMerchandisingProgram,
  localeStorageLanguageKey,
} from 'config/config';
import { getQueryStrings, validateEmail } from 'services/Utilities/Utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { brpClubLogin, brpClubpatchProfile, login, patchProfile, updateProfile } from 'redux/Account/AccountAction';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { useDealers } from 'services/Data/DealerServices';
import { LanguageEnumValue } from 'enums/LanguageEnum';
import { useQueryClient } from '@tanstack/react-query';
import {
  getIsServiceWorkerUpdatePending,
  SERVICE_WORKER_UPDATE_PENDING_CHANGED_EVENT,
} from 'services/Utilities/serviceWorkerUpdateGuard';

const useStyles = makeStyles(theme => ({
  authThumb: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    padding: 0,
    width: '100%',
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    [theme.breakpoints.up('md')]: {
      width: '50%',
      order: 2,
      objectFit: 'cover',
    },
  },
  maxWidth50: {
    width: '50%',
  },
  authContent: {
    padding: 30,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: props => (props.variant === 'default' ? '50%' : '100%'),
      order: 1,
    },
    [theme.breakpoints.up('xl')]: {
      padding: 50,
    },
  },
  titleRoot: {
    marginBottom: 14,
    color: `${theme.palette.secondary.main}`,
    fontSize: '1.75rem',
  },
  textFieldRoot: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.dark, 0.12),
    },
  },
  formcontrolLabelRoot: {
    '& .MuiFormControlLabel-label': {
      [theme.breakpoints.down('md')]: {
        fontSize: 12,
      },
    },
  },
  imageLogoAndLanguage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  langRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 5,
    paddingRight: 5,
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      paddingLeft: 15,
      paddingRight: 15,
    },
  },
  forgotPassword: {
    textDecoration: 'none',
    color: `${theme.palette.primary.main} !important`,
    textDecorationColor: theme.palette.primary.main,
    '& :hover': {
      color: `${theme.palette.primary.dark} !important`,
    },
    '& a': {
      color: `${theme.palette.primary.main} !important`,
      fontWeight: 600,
    },
  },
}));
//variant = 'default', 'standard'
const SignIn = ({ variant = 'default', wrapperVariant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const passwordRef = useRef();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const classes = useStyles({ variant });
  const hist = useHistory();
  const { t } = useTranslation();

  const accountReducer = useSelector(state => state.AccountReducer);
  const queryStrings = getQueryStrings();
  const returnUrl = queryStrings['returnUrl'];
  const hasSsoError = queryStrings['ssoError'] === '1';
  const isEmployeeSelectorRequested = queryStrings['employeeSelector'] === '1';
  const [isServiceWorkerUpdatePending, setIsServiceWorkerUpdatePending] = useState(getIsServiceWorkerUpdatePending());
  const isEmployeeDealerSelectionMode = IsImpersonationMode && accountReducer.isLoggedIn && isEmployeeSelectorRequested;
  const { data: availableDealers, isLoading: isLoadingDealers } = useDealers(
    accountReducer.isLoggedIn,
    isEmployeeDealerSelectionMode,
  );

  const normalizedAvailableDealers = useMemo(() => {
    if (Array.isArray(availableDealers)) {
      return availableDealers;
    }

    if (Array.isArray(availableDealers?.items)) {
      return availableDealers.items;
    }

    if (Array.isArray(availableDealers?.data)) {
      return availableDealers.data;
    }

    return [];
  }, [availableDealers]);

  const dealerOptions = useMemo(
    () =>
      normalizedAvailableDealers.map(dealer => ({
        id: dealer.id,
        isRelatedToCurrentUser: dealer.isRelatedToCurrentUser === true,
        languageEnum: dealer.language,
        name: `${dealer.identifier || ''} - ${dealer.customerName || ''}`,
      })),
    [normalizedAvailableDealers],
  );

  const shouldGroupDealers = useMemo(() => {
    const hasRelatedDealers = dealerOptions.some(dealer => dealer.isRelatedToCurrentUser);
    const hasOtherDealers = dealerOptions.some(dealer => !dealer.isRelatedToCurrentUser);
    return hasRelatedDealers && hasOtherDealers;
  }, [dealerOptions]);

  const selectedDealer = dealerOptions.find(dealer => dealer.id === selectedDealerId) || null;

  const onSubmit = () => {
    dispatch((IsClub ? brpClubLogin : login)(email, password)).then(user => {
      var preferredLanguage = user.person?.preferredLanguage;

      if (preferredLanguage && i18n.language !== preferredLanguage) {
        localStorage.setItem(localeStorageLanguageKey, preferredLanguage);
        i18n.changeLanguage(preferredLanguage);
        if (preferredLanguage === 'fr') moment.locale('fr-CA');
        else moment.locale('en-CA');
      }

      if (IsImpersonationMode && (IsLocalDevOrServerDev || IsClub) && BypassImpersonationSsoInDev) {
        hist.push(`/signin?employeeSelector=1&returnUrl=${returnUrl}`);
      } else {
        hist.push(`${returnUrl || homePage}`);
      }
    });
  };

  const redirectToSso = useCallback(() => {
    const requestedReturnUrl = returnUrl || '/';
    const ssoRoute = IsMerchandisingProgram
      ? 'merchandising-program/login'
      : IsDSM
      ? 'dsm/login'
      : IsEmployee
      ? 'employee-dealer-portal/login'
      : 'dealer/login';

    window.location.href = IsDev
      ? `https://brp-sso.ngrok.io/authentication/sso/${ssoRoute}?returnUrl=${requestedReturnUrl}`
      : `${HOST}/authentication/sso/${ssoRoute}?returnUrl=${requestedReturnUrl}`;
  }, [returnUrl]);

  const handleEmployeeDealerSelection = () => {
    if (!selectedDealer?.id) {
      return;
    }
    const dealerLanguageEnum =
      selectedDealer.languageEnum === LanguageEnumValue.FRENCH ? LanguageEnumValue.FRENCH : LanguageEnumValue.ENGLISH;
    const dealerLanguageCode = dealerLanguageEnum === LanguageEnumValue.FRENCH ? 'fr' : 'en';

    dispatch(
      (IsClub ? brpClubpatchProfile : patchProfile)({
        fieldValues: [
          {
            value: dealerLanguageEnum,
            fieldName: 'person.languageEnum',
          },
          {
            value: selectedDealer.id,
            fieldName: 'temporaryDealer_Id',
          },
        ],
      }),
    ).then(() => {
      localStorage.setItem(localeStorageLanguageKey, dealerLanguageCode);
      i18n.changeLanguage(dealerLanguageCode);
      if (dealerLanguageCode === 'fr') moment.locale('fr-CA');
      else moment.locale('en-CA');

      // Keep selected dealer in local redux profile for immediate UI consistency.
      dispatch(
        updateProfile({
          ...(accountReducer?.profile || {}),
          person: {
            ...(accountReducer?.profile?.person || {}),
            languageEnum: dealerLanguageEnum,
          },
          dealer_Id: selectedDealer.id,
          temporaryDealer_Id: selectedDealer.id,
        }),
      );

      queryClient.removeQueries({ queryKey: ['dealer'] });
      queryClient.removeQueries({ queryKey: ['dealerPortalSectionLandingPage'] });
      queryClient.invalidateQueries();
      if (IsClub) {
        hist.push(`/dealer/home-page`);
        return;
      }
      hist.push(`${returnUrl || homePage}`);
    });
  };

  useEffect(() => {
    if (isEmployeeDealerSelectionMode && !selectedDealerId && dealerOptions.length > 0) {
      const preferredDealerId = accountReducer?.profile?.temporaryDealer_Id || dealerOptions[0].id;
      setSelectedDealerId(preferredDealerId);
    }
  }, [accountReducer?.profile?.temporaryDealer_Id, dealerOptions, isEmployeeDealerSelectionMode, selectedDealerId]);

  useEffect(() => {
    const handlePendingStateChanged = () => {
      setIsServiceWorkerUpdatePending(getIsServiceWorkerUpdatePending());
    };

    window.addEventListener(SERVICE_WORKER_UPDATE_PENDING_CHANGED_EVENT, handlePendingStateChanged);

    return () => {
      window.removeEventListener(SERVICE_WORKER_UPDATE_PENDING_CHANGED_EVENT, handlePendingStateChanged);
    };
  }, []);

  useEffect(() => {
    const isImpersonationDevBypassEnabled =
      (IsImpersonationMode && IsLocalDevOrServerDev && BypassImpersonationSsoInDev) || IsClub;

    const shouldUseSsoRedirect =
      !isImpersonationDevBypassEnabled &&
      (!IsLocalDevOrServerDev || IsImpersonationMode) &&
      !accountReducer.isLoggedIn &&
      !isEmployeeSelectorRequested &&
      !isServiceWorkerUpdatePending;

    if (shouldUseSsoRedirect && !hasSsoError) {
      // When in dev, you need to have ngrok running
      redirectToSso();
    }
  }, [accountReducer.isLoggedIn, hasSsoError, isEmployeeSelectorRequested, isServiceWorkerUpdatePending, redirectToSso]);

  let errorMessage = '';
  if (
    accountReducer.error &&
    accountReducer.error.response &&
    accountReducer.error.response.data &&
    accountReducer.error.response.data.message
  ) {
    errorMessage = t(accountReducer.error.response.data.message);
  } else if (
    accountReducer.error &&
    accountReducer.error.response === undefined &&
    accountReducer.error.message !== undefined
  ) {
    errorMessage = t(accountReducer.error.message);
  } else if (hasSsoError) {
    errorMessage = t('employee_sso_access_denied');
  }

  if (isEmployeeDealerSelectionMode) {
    return (
      <AuthWrapper variant={wrapperVariant}>
        <Hidden mdDown>
          <CmtImage className={classes.authThumb} src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>

        <Hidden mdUp>
          <CmtImage src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>
        <Box className={classes.authContent}>
          {errorMessage !== '' && errorMessage ? (
            <Box mb={7}>
              <Alert severity="error">
                <AlertTitle>{t('notification.error')}</AlertTitle>
                {errorMessage}
              </Alert>
            </Box>
          ) : null}
          <Box mb={7} className={classes.imageLogoAndLanguage}>
            <CmtImage className={classes.maxWidth50} src={'/images/brp-logo.png'} />
            <Box className={clsx(classes.langRoot, 'Cmt-i18n-switch')}></Box>
          </Box>
          <Typography component="div" variant="h1" className={classes.titleRoot}>
            {t('employee_select_dealer_title')}
          </Typography>
          <Box mb={4}>
            <Typography variant="body1">
              {IsClub ? t('employee_select_dealer_description_club') : t('employee_select_dealer_description')}
            </Typography>
          </Box>
          <Box mb={4}>
            <CustomAutocomplete
              labelText={t('employee_select_dealer_label')}
              hasSkeleton
              isLoading={isLoadingDealers}
              options={dealerOptions}
              formControlProps={{
                fullWidth: true,
              }}
              autocompleteProps={{
                onChange: (event, value) => {
                  if (value?.id !== selectedDealerId) {
                    setSelectedDealerId(value?.id || null);
                  }
                },
                isOptionEqualToValue: (dealer, value) => dealer.id === value?.id,
                value: selectedDealer,
                disableClearable: true,
                getOptionLabel: dealer => dealer?.name || '',
                groupBy: shouldGroupDealers
                  ? dealer =>
                      dealer.isRelatedToCurrentUser
                        ? t('employee_select_dealer_group_yours')
                        : t('employee_select_dealer_group_other')
                  : undefined,
                renderGroup: params => (
                  <li key={params.key}>
                    <Box px={1.5} py={0.5}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                        {params.group}
                      </Typography>
                    </Box>
                    <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
                  </li>
                ),
                filterOptions: (dealers, state) => {
                  var searchTerms = state.inputValue.toLowerCase().split(' ');
                  return dealers.filter(dealer =>
                    searchTerms.every(searchTerm => dealer.name?.toLowerCase().includes(searchTerm)),
                  );
                },
              }}
            />

            {dealerOptions?.length === 0 && !isLoadingDealers && (
              <Typography variant="body1">{t('employee_select_no_associated_dealers')}</Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
            <Button
              onClick={handleEmployeeDealerSelection}
              variant="contained"
              color="primary"
              disabled={!selectedDealerId || isLoadingDealers}>
              {t('employee_select_dealer_action')}
            </Button>
          </Box>
        </Box>
      </AuthWrapper>
    );
  }

  if (hasSsoError && !accountReducer.isLoggedIn) {
    return (
      <AuthWrapper variant={wrapperVariant}>
        <Hidden mdDown>
          <CmtImage className={classes.authThumb} src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>

        <Hidden mdUp>
          <CmtImage src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>
        <Box className={classes.authContent}>
          <Box mb={7} className={classes.imageLogoAndLanguage}>
            <CmtImage className={classes.maxWidth50} src={'/images/brp-logo.png'} />
          </Box>

          <Box mb={3}>
            <Alert severity="error">
              <AlertTitle>{t('notification.error')}</AlertTitle>
              {t('employee_sso_access_denied')}
            </Alert>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
            <Button onClick={redirectToSso} variant="contained" color="primary">
              {t('appModule.signIn')}
            </Button>
          </Box>
        </Box>
      </AuthWrapper>
    );
  }

  if (!IsLocalDevOrServerDev && !IsClub) {
    return (
      <AuthWrapper variant={wrapperVariant}>
        <Hidden mdDown>
          <CmtImage className={classes.authThumb} src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>

        <Hidden mdUp>
          <CmtImage src={'/images/main-iwd-lanidingartboard-3.avif'} />
        </Hidden>
        <Box className={classes.authContent}>
          <Box xs={12} className={classes.imageLogoAndLanguage}>
            <CmtImage className={classes.maxWidth50} src={'/images/brp-logo.png'} />
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography component="div" variant="h1" className={classes.titleRoot}>
              {t('logging_in')}
            </Typography>
          </Box>

          <ContentLoader />
        </Box>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper variant={wrapperVariant}>
      <Hidden mdDown>
        <CmtImage className={classes.authThumb} src={'/images/main-iwd-lanidingartboard-3.avif'} />
      </Hidden>

      <Hidden mdUp>
        <CmtImage src={'/images/main-iwd-lanidingartboard-3.avif'} />
      </Hidden>
      <Box className={classes.authContent}>
        {errorMessage !== '' && errorMessage ? (
          <Box mb={7}>
            <Alert severity="error">
              <AlertTitle>{t('notification.error')}</AlertTitle>
              {errorMessage}
            </Alert>
          </Box>
        ) : null}
        <Box mb={7} className={classes.imageLogoAndLanguage}>
          <CmtImage className={classes.maxWidth50} src={'/images/brp-logo.png'} />
          <Box className={clsx(classes.langRoot, 'Cmt-i18n-switch')}>{/* <LanguageSwitcher /> */}</Box>
        </Box>
        {!IsClub && (
          <Box>
            <Typography component="div" variant="h1" className={classes.titleRoot}>
              {t('sidebar.appModule.login')}
            </Typography>
            <Box mb={2}>
              <TextField
                label={t('appModule.email')}
                fullWidth
                onChange={event => setEmail(event.target.value)}
                defaultValue={email}
                margin="normal"
                variant="outlined"
                className={classes.textFieldRoot}
              />
            </Box>
            <Box mb={2}>
              <TextField
                type="password"
                label={t('appModule.password')}
                fullWidth
                onChange={event => setPassword(event.target.value)}
                defaultValue={password}
                margin="normal"
                variant="outlined"
                className={classes.textFieldRoot}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (validateEmail(email)) {
                      onSubmit();
                    }
                    passwordRef.current.blur();
                  }
                }}
                inputProps={{
                  ref: passwordRef,
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
              <FormControlLabel
                className={classes.formcontrolLabelRoot}
                control={<Checkbox name="checkedA" />}
                label={t('appModule.rememberMe')}
              />
            </Box>
          </Box>
        )}
        {/* Different buttons according to if IsClub is true */}
        {IsClub ? (
          // Bigger center button for quick access for club users
          <Box>
            <Box display="flex" alignItems="center" justifyContent="center" mb={5}>
              <h2 style={{ marginRight: '20px', fontWeight: 'bold' }}>{t('appModule.welcomeToBrpClub')}</h2>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center" mb={5}>
              <Button onClick={onSubmit} variant="contained" color="primary" size="large" style={{ width: '500px' }}>
                {t('appModule.continue')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
            <Button onClick={onSubmit} variant="contained" color="primary" disabled={!validateEmail(email)}>
              {t('appModule.signIn')}
            </Button>
          </Box>
        )}

        <ContentLoader />
      </Box>
    </AuthWrapper>
  );
};

export default SignIn;
